import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MapTankInfo } from 'src/app/interfaces/mapTankInfo';
import { CarouselPhoto } from 'src/app/shared/components/photo-carousel/photo-carousel.component';
import { UtilsService } from 'src/app/services/utils.service';

/** volumeAlarmStatusType, and the inactive case, mapped onto the status tokens. */
const STATUS_TEXT_CLASSES = ['text-status-green', 'text-status-amber', 'text-status-red'];
const STATUS_COLOR_VARS = ['--green-rgb', '--amber-rgb', '--red-rgb'];
const INACTIVE_TEXT_CLASS = 'text-status-inactive';
const INACTIVE_COLOR_VAR = '--inactive-rgb';

/**
 * The card shown over the map when a marker is clicked. Presentational only:
 * the host (tank-map) owns marker state, data loading and the carousel index,
 * and owns where the card sits. Nothing here touches the Google Maps API - the
 * card is a normal absolutely-positioned sibling of the map, which is what
 * replaced the per-frame OverlayView.draw() the old popup ran on.
 */
@Component({
  selector: 'app-tank-map-card',
  templateUrl: './tank-map-card.component.html',
  styleUrls: ['./tank-map-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TankMapCardComponent {
  @Input()
  set tankInfo(value: MapTankInfo) {
    this._tankInfo = value;
    this.applyStatus(value);
  }
  get tankInfo(): MapTankInfo {
    return this._tankInfo;
  }

  @Input() volumePct: number;
  @Input() photos: CarouselPhoto[] = [];
  @Input() carouselIndex: number;
  @Input() carouselCount: number;

  @Output() cardCloseClick = new EventEmitter<void>();
  @Output() prevCarouselMarker = new EventEmitter<void>();
  @Output() nextCarouselMarker = new EventEmitter<void>();
  @Output() photoClick = new EventEmitter<CarouselPhoto>();

  /** Tailwind status token, so the card follows the same palette as the rest of the app. */
  statusTextClass = STATUS_TEXT_CLASSES[0];

  /**
   * The same status as a literal `rgb(r g b)`. The level chart lightens and
   * darkens whatever it is given, so it cannot take a `var()` - resolved once
   * per tank here rather than in a getter, because reading computed style on
   * every change detection pass is what made the old popup expensive.
   */
  fillColor = '';

  private _tankInfo: MapTankInfo;

  constructor(private _utils: UtilsService) {}

  get hasPhotos(): boolean {
    return (this.photos?.length ?? 0) > 0;
  }

  private applyStatus(tankInfo: MapTankInfo) {
    const status = tankInfo?.volumeAlarmStatusType ?? 0;
    const inactive = !!tankInfo?.showAsInactive;

    this.statusTextClass = inactive
      ? INACTIVE_TEXT_CLASS
      : STATUS_TEXT_CLASSES[status] ?? STATUS_TEXT_CLASSES[0];

    const colorVar = inactive
      ? INACTIVE_COLOR_VAR
      : STATUS_COLOR_VARS[status] ?? STATUS_COLOR_VARS[0];
    const channels = this._utils.getCSSVariableValue(colorVar);
    this.fillColor = channels ? `rgb(${channels})` : '';
  }
}
